import { zodResolver } from '@hookform/resolvers/zod';
import {
  changePasswordInputSchema,
  updateProfileInputSchema,
  type ChangePasswordInput,
  type UpdateProfileInput,
} from '@jira/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldCheck, Upload } from 'lucide-react';
import { useRef, useState, type ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { EmptyState } from '@/components/common/empty-state';
import { PageHeader } from '@/components/common/page-header';
import { UserAvatar } from '@/components/common/user-avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { extractApiErrorMessage } from '@/lib/api';
import { uploadFile } from '@/lib/collab-api';
import {
  changePasswordApi,
  getProfileApi,
  updateProfileApi,
  type ProfileRow,
} from '@/lib/discovery-api';
import { useAuthStore } from '@/stores/auth-store';

const TABS = [
  ['account', 'Account'],
  ['security', 'Security'],
  ['notifications', 'Notifications'],
] as const;

type Tab = (typeof TABS)[number][0];

export function ProfilePage() {
  const [params, setParams] = useSearchParams();
  const tab = ((params.get('tab') as Tab | null) ?? 'account') as Tab;
  const setTab = (next: Tab) => {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    setParams(p, { replace: true });
  };

  const profileQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => getProfileApi(),
  });

  return (
    <div className="mx-auto w-full max-w-4xl p-6">
      <PageHeader
        title="Profile"
        description="Manage your account, password, and notification preferences."
      />

      <div className="-mb-px mb-6 flex flex-wrap gap-1 border-b">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={[
              'rounded-t-md border-b-2 px-4 py-2 text-sm font-medium transition-colors',
              tab === key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            ].join(' ')}
          >
            {label}
          </button>
        ))}
      </div>

      {profileQuery.isLoading ? (
        <Skeleton className="h-60 w-full" />
      ) : profileQuery.isError || !profileQuery.data ? (
        <EmptyState
          title="Could not load profile"
          description={extractApiErrorMessage(profileQuery.error)}
          action={<Button onClick={() => profileQuery.refetch()}>Retry</Button>}
        />
      ) : tab === 'account' ? (
        <AccountTab profile={profileQuery.data} />
      ) : tab === 'security' ? (
        <SecurityTab />
      ) : (
        <NotificationsTab profile={profileQuery.data} />
      )}
    </div>
  );
}

function AccountTab({ profile }: { profile: ProfileRow }) {
  const { success, error } = useToast();
  const qc = useQueryClient();
  const authUser = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const form = useForm<UpdateProfileInput>({
    resolver: zodResolver(updateProfileInputSchema),
    defaultValues: {
      name: profile.name,
      timezone: profile.timezone,
    },
  });

  const mutation = useMutation({
    mutationFn: (values: UpdateProfileInput) => updateProfileApi(values),
    onSuccess: (row) => {
      success('Profile updated');
      qc.setQueryData(['me'], row);
      if (authUser) {
        setUser({
          ...authUser,
          name: row.name,
          avatarAttachmentId: row.avatarAttachmentId,
          timezone: row.timezone,
        });
      }
    },
    onError: (e) => error('Could not save', extractApiErrorMessage(e)),
  });

  const handleAvatarPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      // The attachment service requires an org context; avatars are attached to the user's first
      // accessible organization (the uploader owns the file regardless).
      const orgId = authUser?.memberships[0]?.organizationId;
      if (!orgId) {
        error('Upload failed', 'You must belong to an organization to upload an avatar');
        return;
      }
      const upload = await uploadFile(orgId, file, {});
      const updated = await updateProfileApi({ avatarAttachmentId: upload.id });
      qc.setQueryData(['me'], updated);
      if (authUser) {
        setUser({ ...authUser, avatarAttachmentId: updated.avatarAttachmentId });
      }
      success('Avatar updated');
    } catch (err) {
      error('Upload failed', extractApiErrorMessage(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeAvatar = async () => {
    try {
      const updated = await updateProfileApi({ avatarAttachmentId: null });
      qc.setQueryData(['me'], updated);
      if (authUser) setUser({ ...authUser, avatarAttachmentId: null });
      success('Avatar removed');
    } catch (err) {
      error('Could not remove avatar', extractApiErrorMessage(err));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Account</CardTitle>
        <CardDescription>Your name, avatar, and timezone.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-4">
          <UserAvatar
            name={profile.name}
            email={profile.email}
            avatarAttachmentId={profile.avatarAttachmentId}
            size={72}
          />
          <div className="space-y-1">
            <p className="text-sm font-medium">{profile.name}</p>
            <p className="text-xs text-muted-foreground">{profile.email}</p>
            <div className="flex gap-2 pt-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden />
                ) : (
                  <Upload className="mr-1 h-3 w-3" aria-hidden />
                )}
                Upload avatar
              </Button>
              {profile.avatarAttachmentId ? (
                <Button size="sm" variant="ghost" onClick={removeAvatar}>
                  Remove
                </Button>
              ) : null}
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                hidden
                onChange={handleAvatarPick}
              />
            </div>
          </div>
        </div>

        <form
          onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
          className="space-y-4"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="pr-name">Display name</Label>
              <Input id="pr-name" {...form.register('name')} />
              {form.formState.errors.name ? (
                <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="pr-email">Email</Label>
              <Input id="pr-email" value={profile.email} readOnly disabled />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pr-tz">Timezone</Label>
              <Input id="pr-tz" {...form.register('timezone')} placeholder="UTC" />
              {form.formState.errors.timezone ? (
                <p className="text-xs text-destructive">{form.formState.errors.timezone.message}</p>
              ) : null}
            </div>
          </div>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function NotificationsTab({ profile }: { profile: ProfileRow }) {
  const { success, error } = useToast();
  const qc = useQueryClient();
  const form = useForm<UpdateProfileInput>({
    resolver: zodResolver(updateProfileInputSchema),
    defaultValues: {
      notifyOnAssign: profile.notifyOnAssign,
      notifyOnMention: profile.notifyOnMention,
    },
  });

  const mutation = useMutation({
    mutationFn: (values: UpdateProfileInput) => updateProfileApi(values),
    onSuccess: (row) => {
      success('Preferences saved');
      qc.setQueryData(['me'], row);
    },
    onError: (e) => error('Save failed', extractApiErrorMessage(e)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notifications</CardTitle>
        <CardDescription>Control when we email you.</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
          className="space-y-4"
        >
          <label className="flex items-start gap-3 rounded-md border p-3 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              defaultChecked={profile.notifyOnAssign}
              {...form.register('notifyOnAssign')}
            />
            <span>
              <span className="block font-medium">Email me when I&apos;m assigned an issue</span>
              <span className="block text-xs text-muted-foreground">
                You&apos;ll receive an email every time someone assigns an issue to you.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-3 rounded-md border p-3 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              defaultChecked={profile.notifyOnMention}
              {...form.register('notifyOnMention')}
            />
            <span>
              <span className="block font-medium">Email me when I&apos;m mentioned</span>
              <span className="block text-xs text-muted-foreground">
                We&apos;ll email you when someone @-mentions you in a comment.
              </span>
            </span>
          </label>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save preferences'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function SecurityTab() {
  const { success, error } = useToast();
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const form = useForm<ChangePasswordInput & { confirm: string }>({
    resolver: zodResolver(
      changePasswordInputSchema
        .extend({
          confirm: changePasswordInputSchema.shape.newPassword,
        })
        .refine((data) => data.newPassword === data.confirm, {
          message: 'Passwords do not match',
          path: ['confirm'],
        }),
    ),
    defaultValues: { currentPassword: '', newPassword: '', confirm: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: ChangePasswordInput) =>
      changePasswordApi({
        ...values,
        ...(refreshToken ? { refreshToken } : {}),
      }),
    onSuccess: (res) => {
      success('Password changed', `${res.invalidatedSessions} other session(s) signed out.`);
      form.reset({ currentPassword: '', newPassword: '', confirm: '' });
    },
    onError: (e) => error('Password change failed', extractApiErrorMessage(e)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" aria-hidden /> Security
        </CardTitle>
        <CardDescription>
          Changing your password signs out every other active session.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={form.handleSubmit((values) =>
            mutation.mutate({
              currentPassword: values.currentPassword,
              newPassword: values.newPassword,
            }),
          )}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="cp-current">Current password</Label>
            <Input
              id="cp-current"
              type="password"
              autoComplete="current-password"
              {...form.register('currentPassword')}
            />
            {form.formState.errors.currentPassword ? (
              <p className="text-xs text-destructive">
                {form.formState.errors.currentPassword.message}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-new">New password</Label>
            <Input
              id="cp-new"
              type="password"
              autoComplete="new-password"
              {...form.register('newPassword')}
            />
            <p className="text-[11px] text-muted-foreground">
              Minimum 10 characters, one number, one symbol.
            </p>
            {form.formState.errors.newPassword ? (
              <p className="text-xs text-destructive">
                {form.formState.errors.newPassword.message}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <Input
              id="cp-confirm"
              type="password"
              autoComplete="new-password"
              {...form.register('confirm')}
            />
            {form.formState.errors.confirm ? (
              <p className="text-xs text-destructive">{form.formState.errors.confirm.message}</p>
            ) : null}
          </div>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Updating…' : 'Change password'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
